import { useEffect, useCallback } from 'react'
import { useSettingsStore, MUSIC_TRACKS } from '../store/settingsStore'

class AudioManager {
  private musicAudio: HTMLAudioElement | null = null
  private onTrackEndCallback?: () => void
  private currentTrackSrc: string | null = null

  constructor() {
    // Initialize music audio
    this.musicAudio = new Audio()
    this.musicAudio.volume = 0.5

    // Auto-advance to next track when current track ends
    this.musicAudio.addEventListener('ended', () => {
      this.onTrackEndCallback?.()
    })
  }

  setMusicTrack(src: string) {
    if (this.musicAudio) {
      // Only update if the track actually changed (prevents restarting the same track)
      if (this.currentTrackSrc !== src) {
        const wasPlaying = !this.musicAudio.paused

        this.musicAudio.src = src
        this.currentTrackSrc = src

        // If it was playing before, restart from the beginning
        if (wasPlaying) {
          this.playMusic()
        }
      }
    }
  }

  playMusic() {
    this.musicAudio?.play().catch(console.error)
  }

  pauseMusic() {
    this.musicAudio?.pause()
  }

  setMusicVolume(volume: number) {
    if (this.musicAudio) {
      this.musicAudio.volume = volume
    }
  }

  getCurrentTime(): number {
    return this.musicAudio?.currentTime || 0
  }

  getDuration(): number {
    return this.musicAudio?.duration || 0
  }

  setOnTrackEnd(callback: () => void) {
    this.onTrackEndCallback = callback
  }
}

// Singleton instance
const audioManager = new AudioManager()

/**
 * Global audio hook that keeps music playing across all pages.
 * This should be called once from the App component to ensure
 * continuous playback when music is enabled in settings.
 */
export function usePersistentAudio() {
  const { audio, nextTrack } = useSettingsStore()

  // Update music track when index changes
  useEffect(() => {
    const trackPath = MUSIC_TRACKS[audio.currentTrackIndex]
    if (trackPath) {
      audioManager.setMusicTrack(trackPath)
    }
  }, [audio.currentTrackIndex])

  // Update music volume when settings change
  useEffect(() => {
    audioManager.setMusicVolume(audio.musicVolume)
  }, [audio.musicVolume])

  // Handle music play/pause based on enabled state
  useEffect(() => {
    if (audio.musicEnabled) {
      // Load the current track if needed
      const trackPath = MUSIC_TRACKS[audio.currentTrackIndex]
      if (trackPath && !audioManager.getCurrentTime()) {
        audioManager.setMusicTrack(trackPath)
      }
      audioManager.playMusic()
    } else {
      audioManager.pauseMusic()
    }
  }, [audio.musicEnabled, audio.currentTrackIndex])

  // Set up auto-advance to next track
  useEffect(() => {
    audioManager.setOnTrackEnd(() => {
      nextTrack()
    })
  }, [nextTrack])
}

/**
 * UI-focused audio hook that provides track navigation functions.
 * Used by SettingsPage for the audio controls UI.
 *
 * Note: The actual audio playback is handled by usePersistentAudio in App.tsx
 * to ensure continuous playback across all pages.
 */
export function useAudioManager() {
  const { audio, nextTrack, setTrack } = useSettingsStore()

  const playNextTrack = useCallback(() => {
    nextTrack()
  }, [nextTrack])

  const playPreviousTrack = useCallback(() => {
    useSettingsStore.getState().previousTrack()
  }, [])

  return {
    getCurrentTrack: () => MUSIC_TRACKS[audio.currentTrackIndex],
    getCurrentTrackIndex: () => audio.currentTrackIndex,
    getTotalTracks: () => MUSIC_TRACKS.length,
    playNextTrack,
    playPreviousTrack,
    setTrack,
    getCurrentTime: () => audioManager.getCurrentTime(),
    getDuration: () => audioManager.getDuration(),
  }
}

// Export the singleton for direct access if needed
export { audioManager }

/**
 * Start music if enabled in settings.
 * Call this from a user interaction handler (like button click)
 * to ensure browser autoplay policy is satisfied.
 */
export function startMusicIfEnabled() {
  const { audio } = useSettingsStore.getState()
  if (audio.musicEnabled) {
    const trackPath = MUSIC_TRACKS[audio.currentTrackIndex]
    if (trackPath) {
      audioManager.setMusicTrack(trackPath)
      audioManager.playMusic()
    }
  }
}
